// === FILE HEADER: Chess Slider Game Utilities ===
// This file implements core game logic, board state management, and movement rules for the Chess Slider SPA.
// Structure and section anchors follow project documentation and copilot-instructions.md.
// Last updated: 2025-07-15
//
// === END FILE HEADER ===
// === COPILOT FILE RULES ===
// DO NOT DELETE THE HEADER OR FILE RULES FROM THE FILE
// DO NOT PLACE ANY CODE, HOOKS, OR LOGIC ABOVE IMPORTS. 
// All import statements must be at the very top of the file, before any other code or comments (except for a file-level comment header or license block, if present).
// React hooks (useEffect, useState, etc.) must only be called inside a React function component or custom hook, never at the top level.
// Make a copy of the file in copies folder before doing any edits.
// Updating full large files has been difficult.  You may use the copy to refer to if it helps you.
// After completing edits compare the edited file to the original and make sure nothing other than the intended changes have happened
// If there are unintended changes repair the file
// After user approves the copy may be deleted
// DO NOT USE INLINE STYLES ALL STYLES MUST BE DEFINED IN THE PROPER FILE AND IMPORTED
// === END COPILOT FILE RULES ===
// === Chessboard Color Constants ===
// Use these everywhere for board and UI color consistency
// .square.light
/**
 * File: src/utils/gameUtils.js
 * Description: Core game logic utilities for the Chess Tile Slider (JavaScript version).
 *
 * Architecture Overview:
 * - Board and tile state are managed using concise, modern, and maintainable data structures.
 * - STARTING_SQUARE_MAP: Object keyed by fileRank (e.g., 'e4'), value is { fileRank, tileId, color }.
 * - STARTING_TILE_MAP: Object keyed by tileId, value is:
 *     - For chess piece tiles: { tileId, fileRank, type: 'piece', pieceRef, pieceColor, unicode, displayName }
 *     - For color tiles: { tileId, fileRank, type: 'color', color }
 * - All in-play state is managed via inPlaySquares and inPlayTiles, which are mutable objects updated directly by all utility and movement logic.
 * - All React state updates must use a shallow copy at the top level to ensure React detects changes, but utility functions and movement logic mutate the state objects directly for performance and clarity.
 * - Naming is consistent: use inPlaySquares/inPlayTiles for all in-play state, and pieceRef/pieceColor for chess pieces.
 * - State setters (setInPlaySquares, setInPlayTiles, setEmptySquare, setMoveHistory) are exposed in useGameState for board restoration/reset (see App.js Reset Tiles logic).
 *
 * Utility Functions:
 * - getInitialInPlaySquares(): Returns a deep copy of STARTING_SQUARE_MAP for use as inPlaySquares (for initialization only).
 * - getInitialInPlayTiles(): Returns a deep copy of STARTING_TILE_MAP for use as inPlayTiles (for initialization only).
 * - removeTileFromBoard(): Mutates the provided gameState object directly to remove a tile from the board and update both inPlaySquares and inPlayTiles.
 * - getMovableTileIds(): Returns tile IDs adjacent to a given empty square using the current inPlaySquares mapping.
 *
 * All documentation and code references have been updated to reflect this direct-mutation architecture. Immutability patterns are not used for in-play state.
 *
 * Reset/Restore Pattern:
 * - To restore a previous board state (e.g., Reset Tiles), use the exposed setters from useGameState to set inPlaySquares, inPlayTiles, and emptySquare from a saved snapshot. Always clear moveHistory on reset.
 */

import { TILE_NOTES } from './soundConstants'; export const BOARD_COLOR_DARK = '#D2B48C'; // .square.dark
export const BOARD_COLOR_LIGHT = '#f0d9b5';

// === Section: Chess Piece Definitions ===

// pawn:   { type: "pawn",   unicode: "\u265F", name: "Pawn" },   // ♟
// Static Chess Piece Definitions - SOLID UNICODE (visual reference)
const CHESS_PIECES = {
  king:   { type: "king",   unicode: "\u265A", name: "King" },   // ♚
  queen:  { type: "queen",  unicode: "\u265B", name: "Queen" },  // ♛
  rook:   { type: "rook",   unicode: "\u265C", name: "Rook" },   // ♜
  bishop: { type: "bishop", unicode: "\u265D", name: "Bishop" }, // ♝
  knight: { type: "knight", unicode: "\u265E", name: "Knight" }, // ♞
  white_pawn: { type: "white_pawn", unicode: "\u2659", name: "White Pawn" }, // ♙
  black_pawn: { type: "black_pawn", unicode: "\u265F", name: "Black Pawn" }  // ♟
};

// === Section: Shared Solution Arrays ===

// Shared solution arrays - referenced by multiple tiles
const SOLUTION_ARRAYS = {
  // White pieces
  WHITE_KING: ["e1"],
  WHITE_QUEEN: ["d1"],
  WHITE_ROOKS: ["a1", "h1"],
  WHITE_BISHOPS: ["c1", "f1"],
  WHITE_KNIGHTS: ["b1", "g1"],
  WHITE_PAWNS: ["a2", "b2", "c2", "d2", "e2", "f2", "g2", "h2"],
  // Black pieces
  BLACK_KING: ["e8"],
  BLACK_QUEEN: ["d8"],
  BLACK_ROOKS: ["a8", "h8"],
  BLACK_BISHOPS: ["c8", "f8"],
  BLACK_KNIGHTS: ["b8", "g8"],
  BLACK_PAWNS: ["a7", "b7", "c7", "d7", "e7", "f7", "g7", "h7"],
  // Shared color tile arrays (corrected for chessboard pattern)
  EMPTY_LIGHT_SQUARES: [
    // Rank 6
    'a6', 'c6', 'e6', 'g6',
    // Rank 5
    'b5', 'd5', 'f5', 'h5',
    // Rank 4
    'a4', 'c4', 'e4', 'g4',
    // Rank 3
    'b3', 'd3', 'f3', 'h3'
  ],
  EMPTY_DARK_SQUARES: [
    // Rank 6
    'b6', 'd6', 'f6', 'h6',
    // Rank 5
    'a5', 'c5', 'e5', 'g5',
    // Rank 4
    'b4', 'd4', 'f4', 'h4',
    // Rank 3
    'a3', 'c3', 'e3', 'g3'
  ]
};

// === Section: Board and Tile Static Maps ===
// STARTING_SQUARE_MAP: Object keyed by fileRank, value is { fileRank, tileId, color }
// STARTING_TILE_MAP: Object keyed by tileId, value is { tileId, fileRank, type, ... }
// All in-play state is managed via inPlaySquares and inPlayTiles, initialized as deep copies of these maps.
// All legacy structures and properties have been removed.

export const STARTING_SQUARE_MAP = {
  // Rank 8
  a8: { fileRank: 'a8', tileId: 'bRa8', color: 'cream' },
  b8: { fileRank: 'b8', tileId: 'bNb8', color: 'tan' },
  c8: { fileRank: 'c8', tileId: 'bBc8', color: 'cream' },
  d8: { fileRank: 'd8', tileId: 'bQd8', color: 'tan' },
  e8: { fileRank: 'e8', tileId: 'bKe8', color: 'cream' },
  f8: { fileRank: 'f8', tileId: 'bBf8', color: 'tan' },
  g8: { fileRank: 'g8', tileId: 'bNg8', color: 'cream' },
  h8: { fileRank: 'h8', tileId: 'bRh8', color: 'tan' },
  // Rank 7
  a7: { fileRank: 'a7', tileId: 'bPa7', color: 'tan' },
  b7: { fileRank: 'b7', tileId: 'bPb7', color: 'cream' },
  c7: { fileRank: 'c7', tileId: 'bPc7', color: 'tan' },
  d7: { fileRank: 'd7', tileId: 'bPd7', color: 'cream' },
  e7: { fileRank: 'e7', tileId: 'bPe7', color: 'tan' },
  f7: { fileRank: 'f7', tileId: 'bPf7', color: 'cream' },
  g7: { fileRank: 'g7', tileId: 'bPg7', color: 'tan' },
  h7: { fileRank: 'h7', tileId: 'bPh7', color: 'cream' },
  // Rank 6
  a6: { fileRank: 'a6', tileId: 'cta6', color: 'cream' },
  b6: { fileRank: 'b6', tileId: 'ctb6', color: 'tan' },
  c6: { fileRank: 'c6', tileId: 'ctc6', color: 'cream' },
  d6: { fileRank: 'd6', tileId: 'ctd6', color: 'tan' },
  e6: { fileRank: 'e6', tileId: 'cte6', color: 'cream' },
  f6: { fileRank: 'f6', tileId: 'ctf6', color: 'tan' },
  g6: { fileRank: 'g6', tileId: 'ctg6', color: 'cream' },
  h6: { fileRank: 'h6', tileId: 'cth6', color: 'tan' },
  // Rank 5
  a5: { fileRank: 'a5', tileId: 'cta5', color: 'tan' },
  b5: { fileRank: 'b5', tileId: 'ctb5', color: 'cream' },
  c5: { fileRank: 'c5', tileId: 'ctc5', color: 'tan' },
  d5: { fileRank: 'd5', tileId: 'ctd5', color: 'cream' },
  e5: { fileRank: 'e5', tileId: 'cte5', color: 'tan' },
  f5: { fileRank: 'f5', tileId: 'ctf5', color: 'cream' },
  g5: { fileRank: 'g5', tileId: 'ctg5', color: 'tan' },
  h5: { fileRank: 'h5', tileId: 'cth5', color: 'cream' },
  // Rank 4
  a4: { fileRank: 'a4', tileId: 'cta4', color: 'cream' },
  b4: { fileRank: 'b4', tileId: 'ctb4', color: 'tan' },
  c4: { fileRank: 'c4', tileId: 'ctc4', color: 'cream' },
  d4: { fileRank: 'd4', tileId: 'ctd4', color: 'tan' },
  e4: { fileRank: 'e4', tileId: 'cte4', color: 'cream' },
  f4: { fileRank: 'f4', tileId: 'ctf4', color: 'tan' },
  g4: { fileRank: 'g4', tileId: 'ctg4', color: 'cream' },
  h4: { fileRank: 'h4', tileId: 'cth4', color: 'tan' },
  // Rank 3
  a3: { fileRank: 'a3', tileId: 'cta3', color: 'tan' },
  b3: { fileRank: 'b3', tileId: 'ctb3', color: 'cream' },
  c3: { fileRank: 'c3', tileId: 'ctc3', color: 'tan' },
  d3: { fileRank: 'd3', tileId: 'ctd3', color: 'cream' },
  e3: { fileRank: 'e3', tileId: 'cte3', color: 'tan' },
  f3: { fileRank: 'f3', tileId: 'ctf3', color: 'cream' },
  g3: { fileRank: 'g3', tileId: 'ctg3', color: 'tan' },
  h3: { fileRank: 'h3', tileId: 'cth3', color: 'cream' },
  // Rank 2
  a2: { fileRank: 'a2', tileId: 'wPa2', color: 'cream' },
  b2: { fileRank: 'b2', tileId: 'wPb2', color: 'tan' },
  c2: { fileRank: 'c2', tileId: 'wPc2', color: 'cream' },
  d2: { fileRank: 'd2', tileId: 'wPd2', color: 'tan' },
  e2: { fileRank: 'e2', tileId: 'wPe2', color: 'cream' },
  f2: { fileRank: 'f2', tileId: 'wPf2', color: 'tan' },
  g2: { fileRank: 'g2', tileId: 'wPg2', color: 'cream' },
  h2: { fileRank: 'h2', tileId: 'wPh2', color: 'tan' },
  // Rank 1
  a1: { fileRank: 'a1', tileId: 'wRa1', color: 'tan' },
  b1: { fileRank: 'b1', tileId: 'wNb1', color: 'cream' },
  c1: { fileRank: 'c1', tileId: 'wBc1', color: 'tan' },
  d1: { fileRank: 'd1', tileId: 'wQd1', color: 'cream' },
  e1: { fileRank: 'e1', tileId: 'wKe1', color: 'tan' },
  f1: { fileRank: 'f1', tileId: 'wBf1', color: 'cream' },
  g1: { fileRank: 'g1', tileId: 'wNg1', color: 'tan' },
  h1: { fileRank: 'h1', tileId: 'wRh1', color: 'cream' }
};

export const STARTING_TILE_MAP = {
  // Piece tiles (full metadata for rendering)
  bRa8: { tileId: 'bRa8', fileRank: 'a8', type: 'piece', pieceRef: 'rook', pieceColor: 'black', unicode: CHESS_PIECES.rook.unicode, displayName: 'Black Rook', solved: SOLUTION_ARRAYS.BLACK_ROOKS, note: TILE_NOTES[0] },
  bNb8: { tileId: 'bNb8', fileRank: 'b8', type: 'piece', pieceRef: 'knight', pieceColor: 'black', unicode: CHESS_PIECES.knight.unicode, displayName: 'Black Knight', solved: SOLUTION_ARRAYS.BLACK_KNIGHTS, note: TILE_NOTES[1] },
  bBc8: { tileId: 'bBc8', fileRank: 'c8', type: 'piece', pieceRef: 'bishop', pieceColor: 'black', unicode: CHESS_PIECES.bishop.unicode, displayName: 'Black Bishop', solved: SOLUTION_ARRAYS.BLACK_BISHOPS, note: TILE_NOTES[2] },
  bQd8: { tileId: 'bQd8', fileRank: 'd8', type: 'piece', pieceRef: 'queen', pieceColor: 'black', unicode: CHESS_PIECES.queen.unicode, displayName: 'Black Queen', solved: SOLUTION_ARRAYS.BLACK_QUEEN, note: TILE_NOTES[3] },
  bKe8: { tileId: 'bKe8', fileRank: 'e8', type: 'piece', pieceRef: 'king', pieceColor: 'black', unicode: CHESS_PIECES.king.unicode, displayName: 'Black King', solved: SOLUTION_ARRAYS.BLACK_KING, note: TILE_NOTES[4] },
  bBf8: { tileId: 'bBf8', fileRank: 'f8', type: 'piece', pieceRef: 'bishop', pieceColor: 'black', unicode: CHESS_PIECES.bishop.unicode, displayName: 'Black Bishop', solved: SOLUTION_ARRAYS.BLACK_BISHOPS, note: TILE_NOTES[5] },
  bNg8: { tileId: 'bNg8', fileRank: 'g8', type: 'piece', pieceRef: 'knight', pieceColor: 'black', unicode: CHESS_PIECES.knight.unicode, displayName: 'Black Knight', solved: SOLUTION_ARRAYS.BLACK_KNIGHTS, note: TILE_NOTES[6] },
  bRh8: { tileId: 'bRh8', fileRank: 'h8', type: 'piece', pieceRef: 'rook', pieceColor: 'black', unicode: CHESS_PIECES.rook.unicode, displayName: 'Black Rook', solved: SOLUTION_ARRAYS.BLACK_ROOKS, note: TILE_NOTES[7] },
  bPa7: { tileId: 'bPa7', fileRank: 'a7', type: 'piece', pieceRef: 'black_pawn', pieceColor: 'black', unicode: CHESS_PIECES.black_pawn.unicode, displayName: 'Black Pawn', solved: SOLUTION_ARRAYS.BLACK_PAWNS, note: TILE_NOTES[8] },
  bPb7: { tileId: 'bPb7', fileRank: 'b7', type: 'piece', pieceRef: 'black_pawn', pieceColor: 'black', unicode: CHESS_PIECES.black_pawn.unicode, displayName: 'Black Pawn', solved: SOLUTION_ARRAYS.BLACK_PAWNS, note: TILE_NOTES[9] },
  bPc7: { tileId: 'bPc7', fileRank: 'c7', type: 'piece', pieceRef: 'black_pawn', pieceColor: 'black', unicode: CHESS_PIECES.black_pawn.unicode, displayName: 'Black Pawn', solved: SOLUTION_ARRAYS.BLACK_PAWNS, note: TILE_NOTES[10] },
  bPd7: { tileId: 'bPd7', fileRank: 'd7', type: 'piece', pieceRef: 'black_pawn', pieceColor: 'black', unicode: CHESS_PIECES.black_pawn.unicode, displayName: 'Black Pawn', solved: SOLUTION_ARRAYS.BLACK_PAWNS, note: TILE_NOTES[11] },
  bPe7: { tileId: 'bPe7', fileRank: 'e7', type: 'piece', pieceRef: 'black_pawn', pieceColor: 'black', unicode: CHESS_PIECES.black_pawn.unicode, displayName: 'Black Pawn', solved: SOLUTION_ARRAYS.BLACK_PAWNS, note: TILE_NOTES[12] },
  bPf7: { tileId: 'bPf7', fileRank: 'f7', type: 'piece', pieceRef: 'black_pawn', pieceColor: 'black', unicode: CHESS_PIECES.black_pawn.unicode, displayName: 'Black Pawn', solved: SOLUTION_ARRAYS.BLACK_PAWNS, note: TILE_NOTES[13] },
  bPg7: { tileId: 'bPg7', fileRank: 'g7', type: 'piece', pieceRef: 'black_pawn', pieceColor: 'black', unicode: CHESS_PIECES.black_pawn.unicode, displayName: 'Black Pawn', solved: SOLUTION_ARRAYS.BLACK_PAWNS, note: TILE_NOTES[14] },
  bPh7: { tileId: 'bPh7', fileRank: 'h7', type: 'piece', pieceRef: 'black_pawn', pieceColor: 'black', unicode: CHESS_PIECES.black_pawn.unicode, displayName: 'Black Pawn', solved: SOLUTION_ARRAYS.BLACK_PAWNS, note: TILE_NOTES[15] },
  wPa2: { tileId: 'wPa2', fileRank: 'a2', type: 'piece', pieceRef: 'white_pawn', pieceColor: 'white', unicode: CHESS_PIECES.white_pawn.unicode, displayName: 'White Pawn', solved: SOLUTION_ARRAYS.WHITE_PAWNS, note: TILE_NOTES[48] },
  wPb2: { tileId: 'wPb2', fileRank: 'b2', type: 'piece', pieceRef: 'white_pawn', pieceColor: 'white', unicode: CHESS_PIECES.white_pawn.unicode, displayName: 'White Pawn', solved: SOLUTION_ARRAYS.WHITE_PAWNS, note: TILE_NOTES[49] },
  wPc2: { tileId: 'wPc2', fileRank: 'c2', type: 'piece', pieceRef: 'white_pawn', pieceColor: 'white', unicode: CHESS_PIECES.white_pawn.unicode, displayName: 'White Pawn', solved: SOLUTION_ARRAYS.WHITE_PAWNS, note: TILE_NOTES[50] },
  wPd2: { tileId: 'wPd2', fileRank: 'd2', type: 'piece', pieceRef: 'white_pawn', pieceColor: 'white', unicode: CHESS_PIECES.white_pawn.unicode, displayName: 'White Pawn', solved: SOLUTION_ARRAYS.WHITE_PAWNS, note: TILE_NOTES[51] },
  wPe2: { tileId: 'wPe2', fileRank: 'e2', type: 'piece', pieceRef: 'white_pawn', pieceColor: 'white', unicode: CHESS_PIECES.white_pawn.unicode, displayName: 'White Pawn', solved: SOLUTION_ARRAYS.WHITE_PAWNS, note: TILE_NOTES[52] },
  wPf2: { tileId: 'wPf2', fileRank: 'f2', type: 'piece', pieceRef: 'white_pawn', pieceColor: 'white', unicode: CHESS_PIECES.white_pawn.unicode, displayName: 'White Pawn', solved: SOLUTION_ARRAYS.WHITE_PAWNS, note: TILE_NOTES[53] },
  wPg2: { tileId: 'wPg2', fileRank: 'g2', type: 'piece', pieceRef: 'white_pawn', pieceColor: 'white', unicode: CHESS_PIECES.white_pawn.unicode, displayName: 'White Pawn', solved: SOLUTION_ARRAYS.WHITE_PAWNS, note: TILE_NOTES[54] },
  wPh2: { tileId: 'wPh2', fileRank: 'h2', type: 'piece', pieceRef: 'white_pawn', pieceColor: 'white', unicode: CHESS_PIECES.white_pawn.unicode, displayName: 'White Pawn', solved: SOLUTION_ARRAYS.WHITE_PAWNS, note: TILE_NOTES[55] },
  wRa1: { tileId: 'wRa1', fileRank: 'a1', type: 'piece', pieceRef: 'rook', pieceColor: 'white', unicode: CHESS_PIECES.rook.unicode, displayName: 'White Rook', solved: SOLUTION_ARRAYS.WHITE_ROOKS, note: TILE_NOTES[56] },
  wNb1: { tileId: 'wNb1', fileRank: 'b1', type: 'piece', pieceRef: 'knight', pieceColor: 'white', unicode: CHESS_PIECES.knight.unicode, displayName: 'White Knight', solved: SOLUTION_ARRAYS.WHITE_KNIGHTS, note: TILE_NOTES[57] },
  wBc1: { tileId: 'wBc1', fileRank: 'c1', type: 'piece', pieceRef: 'bishop', pieceColor: 'white', unicode: CHESS_PIECES.bishop.unicode, displayName: 'White Bishop', solved: SOLUTION_ARRAYS.WHITE_BISHOPS, note: TILE_NOTES[58] },
  wQd1: { tileId: 'wQd1', fileRank: 'd1', type: 'piece', pieceRef: 'queen', pieceColor: 'white', unicode: CHESS_PIECES.queen.unicode, displayName: 'White Queen', solved: SOLUTION_ARRAYS.WHITE_QUEEN, note: TILE_NOTES[59] },
  wKe1: { tileId: 'wKe1', fileRank: 'e1', type: 'piece', pieceRef: 'king', pieceColor: 'white', unicode: CHESS_PIECES.king.unicode, displayName: 'White King', solved: SOLUTION_ARRAYS.WHITE_KING, note: TILE_NOTES[60] },
  wBf1: { tileId: 'wBf1', fileRank: 'f1', type: 'piece', pieceRef: 'bishop', pieceColor: 'white', unicode: CHESS_PIECES.bishop.unicode, displayName: 'White Bishop', solved: SOLUTION_ARRAYS.WHITE_BISHOPS, note: TILE_NOTES[61] },
  wNg1: { tileId: 'wNg1', fileRank: 'g1', type: 'piece', pieceRef: 'knight', pieceColor: 'white', unicode: CHESS_PIECES.knight.unicode, displayName: 'White Knight', solved: SOLUTION_ARRAYS.WHITE_KNIGHTS, note: TILE_NOTES[62] },
  wRh1: { tileId: 'wRh1', fileRank: 'h1', type: 'piece', pieceRef: 'rook', pieceColor: 'white', unicode: CHESS_PIECES.rook.unicode, displayName: 'White Rook', solved: SOLUTION_ARRAYS.WHITE_ROOKS, note: TILE_NOTES[63] },
  // Color tiles (ranks 3-6)
  cta3: { tileId: 'cta3', fileRank: 'a3', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[32] },
  ctb3: { tileId: 'ctb3', fileRank: 'b3', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[33] },
  ctc3: { tileId: 'ctc3', fileRank: 'c3', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[34] },
  ctd3: { tileId: 'ctd3', fileRank: 'd3', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[35] },
  cte3: { tileId: 'cte3', fileRank: 'e3', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[36] },
  ctf3: { tileId: 'ctf3', fileRank: 'f3', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[37] },
  ctg3: { tileId: 'ctg3', fileRank: 'g3', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[38] },
  cth3: { tileId: 'cth3', fileRank: 'h3', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[39] },
  cta4: { tileId: 'cta4', fileRank: 'a4', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[40] },
  ctb4: { tileId: 'ctb4', fileRank: 'b4', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[41] },
  ctc4: { tileId: 'ctc4', fileRank: 'c4', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[42] },
  ctd4: { tileId: 'ctd4', fileRank: 'd4', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[43] },
  cte4: { tileId: 'cte4', fileRank: 'e4', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[44] },
  ctf4: { tileId: 'ctf4', fileRank: 'f4', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[45] },
  ctg4: { tileId: 'ctg4', fileRank: 'g4', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[46] },
  cth4: { tileId: 'cth4', fileRank: 'h4', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[47] },
  cta5: { tileId: 'cta5', fileRank: 'a5', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[24] },
  ctb5: { tileId: 'ctb5', fileRank: 'b5', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[25] },
  ctc5: { tileId: 'ctc5', fileRank: 'c5', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[26] },
  ctd5: { tileId: 'ctd5', fileRank: 'd5', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[27] },
  cte5: { tileId: 'cte5', fileRank: 'e5', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[28] },
  ctf5: { tileId: 'ctf5', fileRank: 'f5', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[29] },
  ctg5: { tileId: 'ctg5', fileRank: 'g5', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[30] },
  cth5: { tileId: 'cth5', fileRank: 'h5', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[31] },
  cta6: { tileId: 'cta6', fileRank: 'a6', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[16] },
  ctb6: { tileId: 'ctb6', fileRank: 'b6', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[17] },
  ctc6: { tileId: 'ctc6', fileRank: 'c6', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[18] },
  ctd6: { tileId: 'ctd6', fileRank: 'd6', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[19] },
  cte6: { tileId: 'cte6', fileRank: 'e6', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[20] },
  ctf6: { tileId: 'ctf6', fileRank: 'f6', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[21] },
  ctg6: { tileId: 'ctg6', fileRank: 'g6', type: 'color', color: 'cream', solved: SOLUTION_ARRAYS.EMPTY_LIGHT_SQUARES, note: TILE_NOTES[22] },
  cth6: { tileId: 'cth6', fileRank: 'h6', type: 'color', color: 'tan', solved: SOLUTION_ARRAYS.EMPTY_DARK_SQUARES, note: TILE_NOTES[23] },
};


// === Section: In-Play State Initialization ===
/**
 * Returns a deep copy of the starting square map for use as inPlaySquares.
 * This function is only used for initialization. All in-play state is mutated directly after initialization.
 */
export function getInitialInPlaySquares() {
  return JSON.parse(JSON.stringify(STARTING_SQUARE_MAP));
}

/**
 * Returns a deep copy of the starting tile map for use as inPlayTiles.
 * This function is only used for initialization. All in-play state is mutated directly after initialization.
 */
export function getInitialInPlayTiles() {
  return JSON.parse(JSON.stringify(STARTING_TILE_MAP));
}

// Export the chess pieces for reference
export { CHESS_PIECES };

// === Section: Export EMPTY_LIGHT_SQUARES and EMPTY_DARK_SQUARES for empty square selection ===
// Only color tile squares in ranks 3-6 (middle of the board)
export const EMPTY_LIGHT_SQUARES = [
  // Rank 6
  'a6', 'c6', 'e6', 'g6',
  // Rank 5
  'b5', 'd5', 'f5', 'h5',
  // Rank 4
  'a4', 'c4', 'e4', 'g4',
  // Rank 3
  'b3', 'd3', 'f3', 'h3'
];

// === Section: Utility to Get Eligible Empty Square Choices by Mode ===
/**
 * Returns the list of eligible empty square fileRanks for the given mode.
 * - In 'easy' mode: only interior color tiles (not on files a/h, i.e., not in NON_EASY_SQUARES)
 * - In 'hard' or 'expert' mode: all color tile squares (ranks 3-6)
 * @param {string} mode - 'easy', 'hard', or 'expert'
 * @returns {string[]} Array of eligible fileRanks
 */
export function getEmptyChoices(mode) {
  const all = [...EMPTY_LIGHT_SQUARES, ...EMPTY_DARK_SQUARES];
  if (mode === 'easy') {
    // Filter out edge squares (files a/h)
    return all.filter(sq => !NON_EASY_SQUARES.includes(sq));
  }
  // For 'hard' and 'expert', all color tile squares are eligible
  return all;
}

export const EMPTY_DARK_SQUARES = [
  // Rank 6
  'b6', 'd6', 'f6', 'h6',
  // Rank 5
  'a5', 'c5', 'e5', 'g5',
  // Rank 4
  'b4', 'd4', 'f4', 'h4',
  // Rank 3
  'a3', 'c3', 'e3', 'g3'
];

// === Section: NON_EASY_SQUARES (Edge Color Tile Squares) ===
// These are the edge color tile squares (files a/h, ranks 3-6) for both light and dark
export const NON_EASY_SQUARES = [
  // Rank 3
  'a3', 'h3',
  // Rank 4
  'a4', 'h4',
  // Rank 5
  'a5', 'h5',
  // Rank 6
  'a6', 'h6'
];

// === Section: Utility to Remove Tile from Board by fileRank ===
/**
 * DEPRECATED: Use the new removeTileFromBoard that mutates gameState directly.
 */

// === Section: Remove Tile From Board and Update Game State ===
/**
 * Removes a tile from the board and updates gameState in place (direct mutation).
 * This function mutates the provided gameState object directly, updating both inPlaySquares and inPlayTiles.
 * This is the required pattern for all in-play state updates in the Chess Tile Slider architecture.
 * @param {Object} gameState - The current game state object (will be mutated).
 * @param {string} selectedTileId - The tileId to remove.
 */
export function removeTileFromBoard(gameState, selectedTileId) {
  const fileRank = gameState.inPlayTiles[selectedTileId]?.fileRank;
  if (!fileRank) return;
  gameState.emptySquare = fileRank;
  gameState.inPlaySquares[fileRank].tileId = null;
  delete gameState.inPlayTiles[selectedTileId];
}

// === Section: Chess Move and Tile Movement Utilities ===
// Implements chess-move-based movement rules for the Chess Tile Slider puzzle.
// - Color tiles: may move only if orthogonally adjacent to the empty square.
// - Piece tiles: may move if orthogonally adjacent OR if the empty square is a valid chess "capture" move for that piece (with line of sight for all except knight).

/**
 * Converts a fileRank string (e.g., 'e4') to file and rank indices.
 * @param {string} fileRank
 * @returns {{file: string, rank: number, fileIdx: number, rankIdx: number}}
 */
function parseFileRank(fileRank) {
  const file = fileRank[0];
  const rank = parseInt(fileRank[1], 10);
  const fileIdx = file.charCodeAt(0) - 'a'.charCodeAt(0);
  const rankIdx = rank - 1;
  return { file, rank, fileIdx, rankIdx };
}

/**
 * Returns true if two squares are orthogonally adjacent.
 * @param {string} squareA
 * @param {string} squareB
 * @returns {boolean}
 */
function isOrthogonallyAdjacent(squareA, squareB) {
  const a = parseFileRank(squareA);
  const b = parseFileRank(squareB);
  return (
    (a.fileIdx === b.fileIdx && Math.abs(a.rankIdx - b.rankIdx) === 1) ||
    (a.rankIdx === b.rankIdx && Math.abs(a.fileIdx - b.fileIdx) === 1)
  );
}

/**
 * Returns true if there are no tiles between fromSquare and toSquare (exclusive).
 * Only for straight lines (rook, bishop, queen).
 * @param {string} fromSquare
 * @param {string} toSquare
 * @param {Object} inPlaySquares
 * @returns {boolean}
 */
function hasLineOfSight(fromSquare, toSquare, inPlaySquares, inPlayTiles) {
  const a = parseFileRank(fromSquare);
  const b = parseFileRank(toSquare);
  const fileStep = Math.sign(b.fileIdx - a.fileIdx);
  const rankStep = Math.sign(b.rankIdx - a.rankIdx);

  // Only allow straight or diagonal lines
  if (
    (fileStep !== 0 && rankStep !== 0 && Math.abs(b.fileIdx - a.fileIdx) !== Math.abs(b.rankIdx - a.rankIdx)) ||
    (fileStep === 0 && rankStep === 0)
  ) {
    return false;
  }

  let curFileIdx = a.fileIdx + fileStep;
  let curRankIdx = a.rankIdx + rankStep;
  while (curFileIdx !== b.fileIdx || curRankIdx !== b.rankIdx) {
    const fileChar = String.fromCharCode('a'.charCodeAt(0) + curFileIdx);
    const rankNum = curRankIdx + 1;
    const sq = `${fileChar}${rankNum}`;
    if (inPlaySquares[sq] && inPlaySquares[sq].tileId) {
      const tileId = inPlaySquares[sq].tileId;
      const tile = inPlayTiles[tileId];
      if (tile && tile.type === 'piece') {
        return false;
      }
    }
    curFileIdx += fileStep;
    curRankIdx += rankStep;
  }
  return true;
}

/**
 * Returns true if the move from fromSquare to toSquare is a valid chess "capture" move for the given piece.
 * @param {Object} pieceTile - The tile object (must be type: 'piece')
 * @param {string} fromSquare
 * @param {string} toSquare
 * @param {Object} inPlaySquares
 * @returns {boolean}
 */
export function isValidChessCaptureMove(pieceTile, fromSquare, toSquare, inPlaySquares, inPlayTiles) {
  const a = parseFileRank(fromSquare);
  const b = parseFileRank(toSquare);
  const df = b.fileIdx - a.fileIdx;
  const dr = b.rankIdx - a.rankIdx;

  switch (pieceTile.pieceRef) {
    case 'rook':
      if ((df === 0 || dr === 0) && hasLineOfSight(fromSquare, toSquare, inPlaySquares, inPlayTiles)) {
        return true;
      }
      break;
    case 'bishop':
      if (Math.abs(df) === Math.abs(dr) && hasLineOfSight(fromSquare, toSquare, inPlaySquares, inPlayTiles)) {
        return true;
      }
      break;
    case 'queen':
      if (
        ((df === 0 || dr === 0) || Math.abs(df) === Math.abs(dr)) &&
        hasLineOfSight(fromSquare, toSquare, inPlaySquares, inPlayTiles)
      ) {
        return true;
      }
      break;
    case 'king':
      if (Math.max(Math.abs(df), Math.abs(dr)) === 1) {
        return true;
      }
      break;
    case 'knight':
      if ((Math.abs(df) === 2 && Math.abs(dr) === 1) || (Math.abs(df) === 1 && Math.abs(dr) === 2)) {
        return true;
      }
      break;
    case 'white_pawn':
    case 'black_pawn': {
      // Pawns may move left/right only if adjacent
      if (Math.abs(df) === 1 && dr === 0) {
        return true;
      }
      // Pawns may move up to 2 ranks up or down if not blocked by a chess piece
      if (df === 0 && Math.abs(dr) >= 1 && Math.abs(dr) <= 2) {
        // Check for blocking pieces in the path
        const step = Math.sign(dr);
        for (let i = 1; i < Math.abs(dr); i++) {
          const intermediateRankIdx = a.rankIdx + i * step;
          const fileChar = String.fromCharCode('a'.charCodeAt(0) + a.fileIdx);
          const rankNum = intermediateRankIdx + 1;
          const sq = `${fileChar}${rankNum}`;
          if (inPlaySquares[sq] && inPlaySquares[sq].tileId) {
            const tileId = inPlaySquares[sq].tileId;
            const tile = inPlayTiles[tileId];
            if (tile && tile.type === 'piece') {
              return false;
            }
          }
        }
        // Check destination square is not blocked by a chess piece (should already be empty, but for safety)
        if (inPlaySquares[toSquare] && inPlaySquares[toSquare].tileId) {
          const tileId = inPlaySquares[toSquare].tileId;
          const tile = inPlayTiles[tileId];
          if (tile && tile.type === 'piece') {
            return false;
          }
        }
        return true;
      }
      break;
    }
    default:
      return false;
  }
  return false;
}

/**
 * Returns true if the tile at fromSquare can move to the empty square at toSquare, according to puzzle rules.
 * - Color tiles: only orthogonally adjacent.
 * - Piece tiles: orthogonally adjacent OR valid chess "capture" move (with line of sight for all except knight).
 * @param {Object} tile - The tile object (from inPlayTiles)
 * @param {string} fromSquare
 * @param {string} toSquare
 * @param {Object} inPlaySquares
 * @returns {boolean}
 */
export function canTileMoveToEmpty(tile, fromSquare, toSquare, inPlaySquares, inPlayTiles) {
  if (!tile || !fromSquare || !toSquare) return false;
  if (tile.type === 'color') {
    return isOrthogonallyAdjacent(fromSquare, toSquare);
  }
  if (tile.type === 'piece') {
    return (
      isOrthogonallyAdjacent(fromSquare, toSquare) ||
      isValidChessCaptureMove(tile, fromSquare, toSquare, inPlaySquares, inPlayTiles)
    );
  }
  return false;
}

/**
 * Returns an array of tile IDs that can move to the empty square, according to puzzle rules.
 * @param {Object} inPlaySquares
 * @param {Object} inPlayTiles
 * @param {string} emptySquare
 * @returns {string[]} - Array of movable tile IDs.
 */
export function getMovableTileIdsByRules(inPlaySquares, inPlayTiles, emptySquare) {
  const movable = [];
  for (const fileRank in inPlaySquares) {
    const tileId = inPlaySquares[fileRank].tileId;
    if (!tileId) continue;
    const tile = inPlayTiles[tileId];
    if (canTileMoveToEmpty(tile, fileRank, emptySquare, inPlaySquares, inPlayTiles)) {
      movable.push(tileId);
    }
  }
  return movable;
}
// === End Section: Chess Move and Tile Movement Utilities ===

// === Section: Board Solved State Check ===
/**
 * Checks if the board is in a solved state (all tiles' isSolved are true).
 * Only tiles with fileRank !== null are considered (the displaced/removed tile is ignored).
 * @param {Object} inPlayTiles - Current in-play tiles mapping (tileId -> tileObj)
 * @returns {boolean} True if solved, false otherwise
 */
export function isBoardSolved(inPlayTiles) {
  for (const tileId in inPlayTiles) {
    const tile = inPlayTiles[tileId];
    if (tile.fileRank !== null && !tile.isSolved) {
      return false;
    }
  }
  return true;
}
// === End Section: Board Solved State Check ===

// === Section: Update isSolved for All Tiles ===
// (Removed: updateAllTilesSolvedState - no longer needed)
// === End Section: Update isSolved for All Tiles ===
